/* click-id-capture.js  -  carry the Google click identifier from the ad click to the enquiry.
 *
 * WHY. An enquiry is not the sale. Somebody asks for an invoice, and the money arrives days or
 * weeks later when they pay it, in Xero, off this site entirely. Google therefore never sees the
 * revenue unless we hand it back, and without that it optimises for the VOLUME of enquiries rather
 * than the ones worth having - on a $15k building that is how a budget disappears.
 *
 * Handing it back needs a key that ties the payment to the click. Google's Enhanced Conversions
 * for Leads accepts either a hashed email or a click id. The forms already collect the email, so
 * this is the second key rather than the only one: it is what lifts the match rate.
 *
 * THREE PARAMETERS, NOT ONE. gclid is the normal web click. gbraid and wbraid are what arrive
 * instead when iOS privacy rules suppress gclid, and a store whose buyers are middle-aged and up
 * on phones will see them. Capturing only gclid would silently lose that traffic.
 *
 * WHY A COOKIE. The click lands on a landing page; the enquiry is submitted on a product page,
 * often minutes and several pages later. The identifier has to survive that walk, so it is stored
 * first-party for 90 days, which is the window Google itself works to.
 *
 * PROGRESSIVE ENHANCEMENT. With JavaScript off the hidden field stays empty and the enquiry still
 * sends. The match then falls back to the hashed email, which is the primary key anyway. Nothing
 * here is allowed to interfere with a form somebody is trying to submit.
 *
 * PRIVACY. This writes ONE first-party cookie holding an advertising identifier. Under the APPs
 * covert collection is unfair collection, so the privacy policy has to say this runs and why.
 * That disclosure is a precondition of shipping it, not a follow-up.
 */
(function () {
  // The snippet that owns the hidden field is rendered once per enquiry form, so this file can be
  // requested more than once on a product page. One fetch, and every execution after the first is
  // a no-op.
  if (window.__nsClickIdCapture) return;
  window.__nsClickIdCapture = true;

  var PARAMS = ['gclid', 'gbraid', 'wbraid'];
  var COOKIE = 'ns_click_id';
  var DAYS = 90;

  // The campaign, carried the same way and for the same reason.
  //
  // A click id says WHICH CLICK. It does not say which platform, and it only exists when the
  // platform issued one. utm_source is what separates paid ChatGPT traffic from the organic
  // ChatGPT referrals that already arrive here - GA4 resolves both into one "AI Assistant"
  // channel otherwise, and we could not tell what we paid for from what we got free.
  //
  // It also survives where a click id does not: an ad platform with nil click parameter, or a
  // cookie-blocked visitor whose UTM still sits in the URL of the page they landed on.
  var UTM = ['utm_source', 'utm_medium', 'utm_campaign'];
  var UTM_COOKIE = 'ns_campaign';

  function readCookie(name) {
    try {
      var parts = document.cookie.split('; ');
      for (var i = 0; i < parts.length; i++) {
        var pair = parts[i].split('=');
        if (pair[0] === name) return decodeURIComponent(pair.slice(1).join('='));
      }
    } catch (e) {
      // Blocked cookies. The email remains the primary key, so this is a downgrade, not a failure.
    }
    return '';
  }

  function writeCookie(name, value, days) {
    try {
      var expires = new Date(Date.now() + days * 864e5).toUTCString();
      var secure = window.location.protocol === 'https:' ? '; Secure' : '';
      document.cookie =
        name + '=' + encodeURIComponent(value) + '; expires=' + expires + '; path=/; SameSite=Lax' + secure;
    } catch (e) {
      // As above.
    }
  }

  // The value is stored as "<param>:<id>" rather than the bare id, because the uploader has to
  // tell Google WHICH identifier it is holding. A gbraid sent as a gclid is rejected.
  function resolve() {
    var search = window.location.search;
    if (search) {
      var qs = new URLSearchParams(search);
      for (var i = 0; i < PARAMS.length; i++) {
        var value = qs.get(PARAMS[i]);
        if (value) {
          var stamped = PARAMS[i] + ':' + value;
          // A fresh click overwrites an older one: last click is what Google attributes to.
          writeCookie(COOKIE, stamped, DAYS);
          return stamped;
        }
      }
    }
    return readCookie(COOKIE);
  }

  // Stored as a single "source|medium|campaign" string rather than three cookies, because the
  // three only mean anything together: a medium with nil source attributes nothing.
  function resolveCampaign() {
    var search = window.location.search;
    if (search) {
      var qs = new URLSearchParams(search);
      if (qs.get('utm_source')) {
        var parts = [];
        for (var i = 0; i < UTM.length; i++) parts.push(qs.get(UTM[i]) || '');
        var stamped = parts.join('|');
        writeCookie(UTM_COOKIE, stamped, DAYS);
        return stamped;
      }
    }
    return readCookie(UTM_COOKIE);
  }

  function setAll(selector, value) {
    if (!value) return;
    var fields = document.querySelectorAll(selector);
    for (var i = 0; i < fields.length; i++) fields[i].value = value;
  }

  function fill() {
    try {
      setAll('input[data-click-id]', resolve());

      var campaign = resolveCampaign();
      if (campaign) {
        var parts = campaign.split('|');
        setAll('input[data-campaign-source]', parts[0]);
        setAll('input[data-campaign-medium]', parts[1]);
        setAll('input[data-campaign-name]', parts[2]);
      }
    } catch (e) {
      // Never let analytics plumbing break a form.
    }
  }

  // The email, stashed at SUBMIT.
  //
  // Enhanced Conversions matches on a hashed email as well as a click id, and the click id can be
  // absent: cookies blocked, JavaScript off, or a platform that issues none. The email is the
  // durable key, and Xero holds the same address, which is what lets a payment reconciled weeks
  // later be matched back to the click.
  //
  // It has to be captured here rather than read at success, because these forms submit natively
  // and reload (see the snippet comment: an AJAX submit bypasses Shopify's reCAPTCHA hook), so by
  // the time the confirmation renders the field the customer typed into is empty again.
  //
  // Stored RAW and same-origin for the seconds between submit and reload, then hashed and deleted
  // on the other side. Hashing here would race the navigation: crypto.subtle is async and the page
  // is already leaving.
  function stashEmail(form) {
    try {
      var el = form.querySelector('input[name="contact[email]"]');
      if (el && el.value) sessionStorage.setItem('ns-enquiry-email', el.value);
    } catch (e) {
      // Private mode. The click id still carries the match.
    }
  }

  // The one-shot marker that makes the confirmation page report an enquiry exactly once. Written at
  // SUBMIT, and consumed by snippets/contact-form-resolver.liquid on the confirmation, so a refresh
  // of the confirmation finds nothing to report while every genuine submit, on any product, writes
  // a fresh one. Keyed by form id AND page, because every product's forms share the same ids.
  function markEnquiry(form) {
    try {
      sessionStorage.setItem('ns-enquiry-pending', form.id + '|' + location.pathname);
    } catch (e) {
      // Blocked storage. The confirmation reports without a marker rather than drop the lead.
    }
  }

  document.addEventListener(
    'submit',
    function (e) {
      var form = e.target;
      if (form && form.classList && form.classList.contains('freight-quote__form')) {
        stashEmail(form);
        markEnquiry(form);
      }
    },
    true
  );

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', fill);
  } else {
    fill();
  }
})();
